import { CacheProvider } from '@emotion/react';
import { loadableReady } from '@loadable/component';
import * as Sentry from '@sentry/browser';
import { createBrowserHistory } from 'history';
import React, { useMemo } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { useSSR } from 'react-i18next';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import 'constants/config';
import get from 'lodash/get';
import createStore from 'store/createStore';
import UniversalApp from './UniversalApp';
import apiClient from './lib/apiClient';
import authorizationProvider from './lib/authorizationProvider';
import './locale';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { STAGE } from './constants/config';
import PusherProvider from './context/pusher';
import pusher from './lib/pusher';
import { logout } from './store/modules/user/actions';
import createEmotionCache from './utils/createEmotionCache';

let isProd = process.env.NODE_ENV === 'production';

const { REACT_APP_UI_URL, REACT_APP_STRIPE_PUB_KEY } = window.APP_CONFIG;

const cache = createEmotionCache();

if (isProd) {
  Sentry.init({
    dsn: 'https://403a5d63b3a844eda846201b73f61437@sentry.io/1818732',
    beforeSend(event) {
      // Check if it is an exception, if so, show the report dialog
      // if (event.exception) {
      //    Sentry.showReportDialog();
      // }
      return event;
    },
    captureUnhandledRejections: false,
    release: process.env.RAVEN_HASH,
    environment: REACT_APP_UI_URL.includes('qa') ? 'qa' : 'production',
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      // Generic error code from errors outside the security sandbox
      // You can delete this if using raven.js > 1.0, which ignores these automatically.
      'Script error.',
      // Avast extension error
      '_avast_submit',
      /ResizeObserver/,
      'Non-Error promise rejection captured with keys: message',
    ],
    denyUrls: [
      // Google Adsense
      /pagead\/js/i,
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /profitwell\.com/i,
    ],
  });
}

export const history = createBrowserHistory();
const initialState = window.PRELOADED_STATE;

const store = createStore(initialState, history);
authorizationProvider(store, apiClient);

let dataLayerInitialized = false;
store.subscribe(() => {
  const {
    user: { user, isLoggedIn },
    settings: { account },
  } = store.getState();
  if (isLoggedIn) {
    if (!dataLayerInitialized && user.user_username && account.account_id) {
      Sentry.setUser({
        username: user.user_username,
        id: user.user_id,
        email: user.account_email,
      });
      const dataLayerParams = {
        user_id: user.user_id,
        user_email: user.user_username,
        first_name: user.user_first_name,
        last_name: user.user_last_name,
        user_name: user.user_first_name + ' ' + user.user_last_name,
        user_api_token: user.user_api_token,
        user_date: String(
          Math.round(new Date(user.user_date).getTime() / 1000),
        ),
        account_email: user.account_email,
        account_phone: user.account_phone,
        account_enabled: user.account_enabled === '1',
        is_active: user.account_enabled,
        member_id: user.member_id,
        user_hash: user.user_intercom_hash,
        twilioCount: user.twilio_count,
        exempt_tax_id: account.exempt_tax_id || 0,
        twilio_number: get(account, 'connections.0.conn_address'),
        account_id: user.account_id,
        account_name: user.account_name,
        account_create_date: String(
          Math.round(new Date(account.account_create_date).getTime() / 1000),
        ),
        account_renewal_date: String(
          Math.round(new Date(account.account_renewal_date).getTime() / 1000),
        ),
        people_greater_5: user.counts.contacts > 5,
        contactCount: user.counts.contacts,
        groupCount: user.counts.groups,
        event: 'user-data-loaded',
      };
      if (window.dataLayer) {
        window.dataLayer.push(dataLayerParams);
        dataLayerInitialized = true;
      }
    }
  } else {
    Sentry.setUser(null);
    if (
      window.dataLayer !== undefined &&
      window.google_tag_manager !== undefined
    ) {
      window.dataLayer.length = 0;
      const gtmContainerReg = /GTM-/i;
      for (const gtmKey of Object.keys(window.google_tag_manager)) {
        if (
          gtmContainerReg.test(gtmKey) &&
          window.google_tag_manager[gtmKey].dataLayer &&
          window.google_tag_manager[gtmKey].dataLayer.reset
        ) {
          window.google_tag_manager[gtmKey].dataLayer.reset();
        }
      }
    }
    dataLayerInitialized = false;
  }
});

const BaseApp = ({ children }) => {
  useSSR(window.initialI18nStore, window.initialLanguage);

  const stripePromise = useMemo(() => loadStripe(REACT_APP_STRIPE_PUB_KEY), []);

  return (
    <HelmetProvider>
      <Router history={history}>
        <Provider store={store}>
          <PusherProvider pusher={pusher}>
            <CacheProvider value={cache}>
              <Elements stripe={stripePromise}>{children}</Elements>
            </CacheProvider>
          </PusherProvider>
        </Provider>
      </Router>
    </HelmetProvider>
  );
};

const container = document.getElementById('root');

function render(Component) {
  loadableReady(() => {
    hydrateRoot(
      container,
      <BaseApp>
        <Component config={window.APP_CONFIG} />
      </BaseApp>,
    );
  });
}

if (module.hot) {
  // Enable webpack hot module replacement for routes
  module.hot.accept('./UniversalApp', () => {
    try {
      const NewApp = require('./UniversalApp').default;
      render(NewApp);
    } catch (error) {
      console.error(`==> 😭  Routes hot reloading error ${error}`);
    }
  });
}

render(UniversalApp);
